import { ElectronApi } from '../electron.api';
import {
  getImageBase64,
  getImageSize,
  isImageFile,
  writeImage
} from '@/utils/fs/image.utils';
import {
  ensureDir,
  fsReadDir,
  isDirectory,
  isEmptyDir,
  isFile,
  testAccess
} from '@/utils/fs/fs-extra.utils';
import { fileToMd5 } from '@/utils/md5.utils';

export class WatchfaceResource {
  private ResourcePath: string;

  constructor(ResourcePath: string) {
    this.ResourcePath = ResourcePath;
  }

  async getImagesData() {
    const fs = ElectronApi.fs;
    const path = ElectronApi.path;
    if (!fs || !path) return;

    const res: StoreResourceItem[] = [];
    const filenames = (await fsReadDir(this.ResourcePath)) ?? [];

    let index = 0;
    // 根据图片总数动态调整并发数，减少内存峰值
    const total = filenames?.length ?? 0;
    const step = total > 300 ? 3 : total > 100 ? 5 : 7;
    console.log(`[image-load] 图片总数: ${total}, 并发数: ${step}`);
    while (index < total) {
      const promises: Promise<StoreResourceItem | undefined>[] = [];
      for (let i = index; i < index + step; i++) {
        const item = filenames[i];
        if (!item) continue;
        const { realpath, basename } = item;

        // 如果是rive文件则跳过
        if (basename.startsWith('_rive')) continue;

        promises.push(
          new Promise((resolve) => {
            const isValidImg = isImageFile(realpath);
            if (!isValidImg) resolve(undefined);

            const item: StoreResourceItem = {
              realpath: realpath,
              basename: basename,
              width: 0,
              height: 0
            };

            getImageBase64(realpath)
              .then((base64) => {
                if (base64) {
                  item['value'] = base64;
                  return getImageSize(undefined, base64) ?? {};
                }
              })
              .then((res) => {
                const { width = 0, height = 0 } = res ?? {};
                item.width = width;
                item.height = height;
              })
              .then(() => {
                resolve(item);
              })
              .catch((e) => {
                resolve(item);
              });
          })
        );
      }
      await Promise.all(promises)
        .then((items) => {
          items.forEach((item) => {
            if (item && item?.width) res.push(item);
          });
        })
        .catch((e) => console.error(e));
      index += step;
      // 让出事件循环，给 GC 回收上一批临时数据的机会
      if (index < total) {
        await new Promise((resolve) => setTimeout(resolve, 0));
      }
    }

    return res;
  }

  /**
   * 写入表盘资源
   * @param images 需要写入的资源
   * @param resourceDir 要写入的目录，默认为表盘资源目录
   * @returns
   */
  async writeImages(
    images: {
      base64: string;
      /**
       * 相对于resource路径的名称
       */
      basename: string;
    }[],
    resourceDir?: string
  ) {
    const fs = ElectronApi.fs;
    const path = ElectronApi.path;
    if (!fs || !path) return;

    const dir = resourceDir ?? this.ResourcePath;

    const promises: Promise<void>[] = [];
    const _saveImage = async (
      dir: string,
      image: { base64: string; basename: string }
    ) => {
      const { base64, basename } = image;
      const imageName = path.join(dir, basename);
      // imageName有可能是 xx/xx/xx.png格式，需要先创建其目录
      const subDirs = basename.split('/');
      subDirs.pop();
      let _dir = dir;
      for (const subDir of subDirs) {
        _dir = path.join(_dir, subDir);
        await ensureDir(_dir);
      }

      if (!(await testAccess(imageName))) await writeImage(imageName, base64);
    };

    try {
      await ensureDir(dir);

      // 写入新资源
      for (const image of images) {
        promises.push(_saveImage(dir, image));
      }
      await Promise.all(promises);
    } catch (error) {
      console.log(error);
    }
  }

  /**
   * 删除无用文件
   * @returns
   */
  async deleteUseleseFiles(
    themesStr: string,
    manifestStr: string,
    resourceDir?: string
  ) {
    const fs = ElectronApi.fs;
    const path = ElectronApi.path;
    if (!fs || !path) return;

    // 在此目录里面的文件不会被删除
    const ignoreDirs = ['_js', '_lua', '_sku'];
    // 名称中包含这些字符串的文件不会被删除
    const ignoreFiles = ['manifest.xml', 'manifest1_5.xml'];

    const dir = resourceDir ? resourceDir : this.ResourcePath;
    const files = await fs.promises.readdir(dir);

    // 依次判断每个文件
    for (const _file of files) {
      const file = path.join(dir, _file);
      if (await isFile(file)) {
        // 判断文件是否需要删除
        // 如果在忽略的目录中
        if (ignoreDirs.some((ignoreDir) => file.includes(ignoreDir))) continue;
        // 如果是忽略的文件
        if (ignoreFiles.some((ignoreFile) => file.endsWith(ignoreFile)))
          continue;

        // 查找相对于资源目录的文件名
        // 如果是在使用中的，只需要判断文件名，不判断全路径

        const _basename = file
          .replace(this.ResourcePath + path.sep, '')
          .split(path.sep)
          .join('/');

        if (themesStr.includes(_basename)) continue;
        if (manifestStr.includes(_basename)) continue;

        // 删除文件，不区分文件格式
        await fs.promises.rm(file);
      } else if (await isDirectory(file)) {
        // 如果是目录，则递归删除
        await this.deleteUseleseFiles(themesStr, manifestStr, file);
      }
    }
  }

  async deleteEmptyDirs() {
    const fs = ElectronApi.fs;
    const path = ElectronApi.path;
    if (!fs || !path) return;
    const resourceDir = this.ResourcePath;

    const _del = async (dir: string) => {
      const files = await fs.promises
        .readdir(dir)
        .then((r) => r.map((item) => path.join(dir, item)));
      for (const file of files) {
        const isEmpty = await isEmptyDir(file);
        if (isEmpty) {
          await fs.promises.rmdir(file);
        } else if (await isDirectory(file)) {
          await _del(file);
        }
      }
    };
    await _del(resourceDir);
  }

  /**
   * 向资源目录增加文件，文件名称等于md5值（保留扩展名）
   * @returns 返回basename
   */
  async addFile(
    sourceFile: string,
    options?: {
      prefix?: string;
      subDirs?: string[];
      md5?: boolean;
      deleteOriginal?: boolean;
    }
  ) {
    const fs = ElectronApi.fs;
    const path = ElectronApi.path;
    if (!fs || !path) return;
    if (!(await isFile(sourceFile))) throw '非文件，不可添加';

    const { prefix, subDirs, md5 = true } = options ?? {};

    const dir = this.ResourcePath;
    const basename = path.basename(sourceFile);

    const name = md5
      ? (prefix ?? '') +
        (await fileToMd5(sourceFile, true)) +
        path.extname(basename)
      : basename;

    const filename = subDirs?.length
      ? path.join(dir, ...subDirs, name)
      : path.join(dir, name);

    if (sourceFile != filename) {
      await ensureDir(path.dirname(filename));
      await fs.promises.cp(sourceFile, filename, { force: true });
      // 删除原文件
      if (options?.deleteOriginal) await fs.promises.rm(sourceFile);
    }
    return subDirs?.length ? [...subDirs, name].join('/') : name;
  }

  /**
   * 删除资源目录中的文件
   * @returns 返回basename
   */
  async deleteFile(basename: string) {
    const fs = ElectronApi.fs;
    const path = ElectronApi.path;
    if (!fs || !path) return;
    const filename = path.join(this.ResourcePath, ...basename.split('/'));
    if (await testAccess(filename)) await fs.promises.rm(filename);
  }

  /**
   * 获取文件本地路径
   * @param xmlFilename xml中使用的文件名
   */
  parseFilename(xmlFilename: string) {
    const fs = ElectronApi.fs;
    const path = ElectronApi.path;
    if (!fs || !path) return;
    const dir = this.ResourcePath;
    const filename = path.join(dir, ...xmlFilename.split('/'));
    return filename;
  }
}
